import React, {
    FormEvent,
    useEffect,
    useState,
} from "react";
import api from "../../../api";
import { IMAGE_URL } from "../../../path";
import "../../../styles/Login.scss";

interface AboutUsItem {
    id: number;
    about_us_id: number;
    type: "why_choose" | "different";
    icon: string | null;
    body: string;
    display_order: number;
}

interface AboutUsItemModalProps {
    item?: AboutUsItem | null;
    type: "why_choose" | "different";
    onClose: () => void;
    onSaved: () => void;
}

const AboutUsItemModal = ({
                              item,
                              type,
                              onClose,
                              onSaved,
                          }: AboutUsItemModalProps) => {
    const isEditing = !!item;

    const [body, setBody] =
        useState("");

    const [icon, setIcon] =
        useState<File | null>(null);

    const [loading, setLoading] =
        useState(false);

    const [error, setError] =
        useState("");

    useEffect(() => {
        if (item) {
            setBody(item.body || "");
        } else {
            setBody("");
        }

        setIcon(null);
        setError("");
    }, [item]);

    const sectionTitle =
        type === "why_choose"
            ? "Why Choose Safari?"
            : "How Is Safari Different From Imitation Vinegar?";

    const getFileUrl = (
        path: string | null
    ) => {
        if (!path) {
            return "";
        }

        if (
            path.startsWith("http://") ||
            path.startsWith("https://")
        ) {
            return path;
        }

        return `${IMAGE_URL}/${path}`;
    };

    const handleSubmit = async (
        event: FormEvent<HTMLFormElement>
    ) => {
        event.preventDefault();

        try {
            setLoading(true);
            setError("");

            const formData =
                new FormData();

            formData.append(
                "body",
                body
            );

            if (icon) {
                formData.append(
                    "icon",
                    icon
                );
            }

            if (isEditing) {
                await api.post(
                    `/admin/about-us/items/${item.id}`,
                    formData
                );
            } else {
                formData.append(
                    "type",
                    type
                );

                await api.post(
                    "/admin/about-us/items",
                    formData
                );
            }

            onSaved();
            onClose();
        } catch (err: any) {
            console.error(
                "Failed to save About Us item:",
                err
            );

            const validationErrors =
                err.response?.data?.errors;

            if (validationErrors) {
                const firstError =
                    Object.values(
                        validationErrors
                    )
                        .flat()
                        .find(
                            (
                                validationError
                            ): validationError is string =>
                                typeof validationError ===
                                "string"
                        );

                setError(
                    firstError ||
                    "Unable to save item. Please try again."
                );
            } else {
                setError(
                    err.response?.data?.message ||
                    "Unable to save item. Please try again."
                );
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="admin-modal-overlay"
            onClick={onClose}
        >
            <div
                className="admin-modal"
                onClick={(event) =>
                    event.stopPropagation()
                }
            >

                <div className="admin-modal-header">

                    <h2>
                        {isEditing
                            ? `Edit ${sectionTitle} item`
                            : `Add ${sectionTitle} item`}
                    </h2>

                    <button
                        type="button"
                        className="admin-modal-close"
                        onClick={onClose}
                        disabled={loading}
                    >
                        &times;
                    </button>

                </div>

                <form onSubmit={handleSubmit}>

                    <div className="admin-modal-content">

                        <div className="form-group">

                            <label htmlFor="about-us-item-body">
                                Body
                            </label>

                            <textarea
                                id="about-us-item-body"
                                value={body}
                                onChange={(event) =>
                                    setBody(
                                        event.target.value
                                    )
                                }
                                rows={5}
                                required
                                disabled={loading}
                            />

                        </div>

                        <div className="form-group">

                            <label htmlFor="about-us-item-icon">
                                Icon
                            </label>

                            {isEditing && item?.icon && (
                                <div
                                    className="admin-about-icon-preview"
                                    style={{
                                        marginBottom: "12px",
                                    }}
                                >
                                    <img
                                        src={getFileUrl(
                                            item.icon
                                        )}
                                        alt=""
                                    />
                                </div>
                            )}

                            <input
                                id="about-us-item-icon"
                                type="file"
                                accept=".svg,image/svg+xml"
                                onChange={(event) =>
                                    setIcon(
                                        event.target.files?.[0] ||
                                        null
                                    )
                                }
                                disabled={loading}
                            />

                            <small>
                                Optional SVG icon. Leave
                                blank to keep the current
                                icon when editing.
                            </small>

                        </div>

                        {error && (
                            <div className="form-error">
                                {error}
                            </div>
                        )}

                    </div>

                    <div className="admin-modal-actions">

                        <button
                            type="button"
                            className="admin-table-button"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            className="admin-primary-button"
                            disabled={loading}
                        >
                            {loading
                                ? "Saving..."
                                : isEditing
                                    ? "Save changes"
                                    : "Add item"}
                        </button>

                    </div>

                </form>

            </div>
        </div>
    );
};

export default AboutUsItemModal;
